import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Box, Paper } from '@mui/material';
import { useQuery } from 'src/hooks/useQuery';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { IOperation, OperationStatus } from 'components/order/model/Operation';
import {
    createOrderCreateItemFromBasketItem,
    IOrderJoinDetails,
    OrderErrorCodes
} from 'components/order/model/Order';
import { EOrderNonceKey, orderApi } from 'components/order/orderApi';
import { OPERATION_POLLING_DELAY, REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { ConfirmDialog } from 'lib/ConfirmDialog';
import { QuantitySelector } from 'lib/QuantitySelector';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { setReturnUrl } from 'store/auth/authActions';
import { partialResetBasket, updateBasketItemQuantity } from 'store/basket/basketActions';
import { getMenu } from 'store/menu/menuActions';
import { setLastRequest } from 'store/request/requestActions';
import { ApplicationState } from 'store/store';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { getOrderNotes } from 'components/location/model/Location';
import { PayHeader } from 'components/pay/header/PayHeader';
import { PayToolbar } from 'components/pay/header/PayToolbar';
import { PayItems } from 'components/pay/order-item/PayItems';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayOrderNotes } from 'components/pay/notes/PayOrderNotes';
import { ELoadingSVG, RandomLoading } from 'lib/animations/RandomLoadingSVG';
import { PayBaseModal } from 'components/pay/modals/PayBaseModal';
import logger from 'lib/logger';

const PREFIX = 'PayBasket';

const classes = {
    viewBasketButton: `${PREFIX}viewBasketButton`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
    maxWidth: REDUCED_CONTENT_MAX_WIDTH,
    overflow: 'scroll',
    [`& .${classes.viewBasketButton}`]: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}));

const StyledLoadingContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    height: '100%',
    paddingBottom: theme.spacing(7),
    overflowY: 'hidden',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
}));

const SPECIAL_ERROR_CODES: string[] = [
    OrderErrorCodes.DUPLICATE_ORDER_NONCE,
    OrderErrorCodes.DUPLICATE_PENDING_ORDER,
    OrderErrorCodes.UPDATING_THE_BILL,
    OrderErrorCodes.FAILED_TO_FIND_ORDER
];

function getItemIdGenerator() {
    let index = 0;
    function generateId() {
        return index++;
    }
    return generateId;
}

export const PayBasket: React.FC = () => {
    const { t } = useTranslation();
    const {
        items,
        notes: notesFromState,
        timeSlot,
        deliveryLocation
    } = useSelector((state: ApplicationState) => state.basket);
    const addItemsToOrderRequest = React.useRef<() => Promise<void>>();
    const [joinOrderAttempted, setJoinOrderAttempted] = React.useState(false);
    const { menu, isLoading: isMenuLoading } = useSelector((state: ApplicationState) => state.menu);
    const { user } = useAuth();
    const { search } = useLocation();
    const { push, getParsedPath } = useLocalHistory();
    const { locationId } = useParams<LocationRouteParams>();
    const [scenario] = useRouteScenario();
    const { isWorkingHour } = useLocationHelpers();
    const orderId = useQuery('orderId');
    const tableNumber = useQuery('tableNumber');
    const checkId = useQuery('checkId');
    const dispatch = useDispatch();
    const { lastRequest } = useSelector((state: ApplicationState) => state.request);
    const { currentLocation, isCurrentLocationLoading } = useSelector(
        (state: ApplicationState) => state.locations
    );

    const [quantitySelectProductIndex, setQuantitySelectProductIndex] = React.useState(-1);
    const [quantity, setQuantity] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false);
    const [error, setError] = React.useState<string>();

    const { logUserEvent } = useGAHelpers();

    React.useEffect(() => {
        getMenu(
            locationId || '',
            timeSlot?.start,
            scenario,
            t('DIALOG_TIMESLOT_CHANGED_MESSAGE'),
            deliveryLocation
        )(dispatch);
    }, [dispatch, locationId, scenario, timeSlot?.start, t, deliveryLocation]);

    const notes = React.useMemo(() => getOrderNotes(currentLocation, scenario), [currentLocation, scenario]);

    const mappedItems: IItemReadResourceV10[] = React.useMemo(() => {
        const generateId = getItemIdGenerator();
        return items.reduce<IItemReadResourceV10[]>((acc, item) => {
            if (menu) {
                const productDetailsFromMenu = menu.productIdToProduct.get(item.productId);
                const category = menu.categories.find(({ id }) => item.categoryId === id);

                const modifiers = item.modifiers?.map(modifier => {
                    const detailedModifier =
                        category?.modifiers?.find(catModifier => catModifier.id === modifier.id) ??
                        menu.modifiers?.find(menuModifier => menuModifier.id === modifier.id);

                    return {
                        ...detailedModifier,
                        options: detailedModifier?.options?.filter(option =>
                            modifier.options.some(opt => opt.id === option.id)
                        )
                    };
                });

                if (productDetailsFromMenu && category && user) {
                    acc.push({
                        ...item,
                        id: generateId(),
                        userId: user.id,
                        productName: productDetailsFromMenu.title,
                        categoryName: category.title,
                        cost: item.price,
                        modifiers
                    } as IItemReadResourceV10);
                }
            }
            return acc;
        }, []);
    }, [items, menu, user]);

    const handleCloseQuantitySelect = React.useCallback(() => {
        setQuantitySelectProductIndex(-1);
    }, []);
    const handleOpenQuantitySelect = React.useCallback(
        (index: number) => {
            setQuantitySelectProductIndex(index);
            setQuantity(mappedItems[index].quantity);
        },
        [mappedItems]
    );
    const handleUpdateItemQuantity = React.useCallback(() => {
        updateBasketItemQuantity(quantitySelectProductIndex, quantity)(dispatch);
        handleCloseQuantitySelect();
    }, [quantitySelectProductIndex, quantity, dispatch, handleCloseQuantitySelect]);

    const handleItemClick = React.useCallback(
        (id: number) => {
            const index = mappedItems.findIndex(item => item.id === id);
            if (index >= 0) {
                handleOpenQuantitySelect(index);
            }
        },
        [handleOpenQuantitySelect, mappedItems]
    );
    const handleClickBack = React.useCallback(() => push(ROUTES.MENU, {}, search), [push, search]);
    const handleToggleConfirmationDialog = React.useCallback(
        () => setIsConfirmationOpen(!isConfirmationOpen),
        [isConfirmationOpen]
    );
    const joinOrderAndRetry = React.useCallback(async () => {
        if ((isDefined(tableNumber) || isDefined(checkId)) && locationId && user) {
            const joiningDetails: IOrderJoinDetails =
                isDefined(checkId) && !isEmptyString(checkId)
                    ? {
                          locationId,
                          checkId
                      }
                    : {
                          locationId,
                          deliveryLocation: tableNumber
                      };
            if (joiningDetails.checkId || joiningDetails.deliveryLocation) {
                setJoinOrderAttempted(true);
                setIsLoading(true);
                try {
                    const response = await orderApi.joinOrder(joiningDetails);
                    orderApi.nonceHandlingByOperationStatus(response.status, EOrderNonceKey.JOIN_ORDER);
                    const getOperationStatus = async () => {
                        if (response.id) {
                            const operation: IOperation = await orderApi.getOperation(
                                response.id?.toString()
                            );
                            switch (operation.status) {
                                case OperationStatus.PENDING:
                                    // Recursive Poll for order completion
                                    setIsLoading(true);
                                    setTimeout(() => {
                                        getOperationStatus();
                                    }, OPERATION_POLLING_DELAY);

                                    orderApi.resetNonceByKey(EOrderNonceKey.JOIN_ORDER);
                                    break;
                                case OperationStatus.ERROR:
                                    if (
                                        operation.errorCode === OrderErrorCodes.CHECK_NOT_FOUND ||
                                        operation.errorCode === OrderErrorCodes.ORDER_NOT_FOUND ||
                                        operation.httpCode === 404
                                    ) {
                                        setError(operation.errorMessage);
                                        setIsLoading(false);
                                    }
                                    break;
                                case OperationStatus.TIMEOUT:
                                    throw new Error(operation.errorMessage || 'Operation error or timeout');
                                case OperationStatus.DONE: {
                                    if (!!operation && addItemsToOrderRequest.current) {
                                        addItemsToOrderRequest.current();
                                    }
                                }
                            }
                        }
                    };
                    if (response.id) {
                        getOperationStatus();
                    }
                } catch (error) {
                    logger.error(error);
                }
            } else {
                setIsLoading(false);
            }
        }
    }, [checkId, locationId, tableNumber, user]);
    const handleOperation = React.useCallback(
        (operationId: string) => {
            async function getOperationStatus() {
                try {
                    setIsLoading(true);
                    setError(undefined);

                    const operation = await orderApi.getOperation(operationId);
                    orderApi.nonceHandlingByOperationStatus(
                        operation.status,
                        EOrderNonceKey.ADD_ITEM_TO_ORDER
                    );
                    switch (operation.status) {
                        case OperationStatus.PENDING:
                            // Recursive Poll for order completion
                            setTimeout(() => {
                                getOperationStatus();
                            }, OPERATION_POLLING_DELAY);

                            break;
                        case OperationStatus.ERROR:
                        case OperationStatus.TIMEOUT:
                            setIsLoading(false);

                            if (!operation.errorCode || !SPECIAL_ERROR_CODES.includes(operation.errorCode)) {
                                setError('Operation error or timeout');
                                handleToggleConfirmationDialog();
                            }
                            if (operation.errorCode === OrderErrorCodes.UPDATING_THE_BILL) {
                                setError(operation.errorMessage);
                                handleToggleConfirmationDialog();
                            }
                            if (
                                operation.errorCode === OrderErrorCodes.FAILED_TO_FIND_ORDER &&
                                !joinOrderAttempted
                            ) {
                                joinOrderAndRetry();
                            }

                            break;
                        case OperationStatus.DONE: {
                            push(ROUTES.QUICKPAY.CHECKOUT, {}, `orderId=${orderId}`, {
                                showAddItemSuccess: true
                            });
                            setIsLoading(false);
                            setReturnUrl(getParsedPath(ROUTES.QUICKPAY.CHECKOUT))(dispatch);
                            // Redirect
                            partialResetBasket(dispatch);
                            break;
                        }
                    }
                } catch (orderStatusError: any) {
                    if (isDefined(orderStatusError?.response && orderStatusError.response.status === 404)) {
                        setError(orderStatusError.response.errorMessage);
                    } else {
                        setError(t('GENERAL_GENERIC_ERROR'));

                        if (!isDefined(orderStatusError?.response)) {
                            setError(t('DIALOG_ORDER_STATUS_UNKNOWN'));
                        }
                    }
                    setIsLoading(false);
                    handleToggleConfirmationDialog();
                }
            }
            getOperationStatus();
        },
        [
            handleToggleConfirmationDialog,
            push,
            orderId,
            getParsedPath,
            dispatch,
            joinOrderAttempted,
            joinOrderAndRetry,
            t
        ]
    );
    const handleAddOrder = React.useCallback(async () => {
        setLastRequest(handleAddOrder)(dispatch);
        addItemsToOrderRequest.current = handleAddOrder;
        setIsLoading(true);
        try {
            logUserEvent(EGAEventName.AddToOrder);
            const orderNotes: string[] = [];
            if (Array.isArray(notes) && !!mappedItems[0]) {
                notes.forEach(note => {
                    const currentNoteValue = notesFromState[note.title];
                    if (!!currentNoteValue) {
                        orderNotes.push(`${note.title}: ${currentNoteValue}`);
                    }
                });
            }

            const { id, status } = await orderApi.addOrderItems(Number(orderId), {
                items: mappedItems.map(createOrderCreateItemFromBasketItem),
                note: orderNotes ? orderNotes.join('; ') : undefined
            });
            if (id && status === OperationStatus.PENDING) {
                handleOperation(id.toString());
            }
        } catch (addToOrderError: any) {
            if (isDefined(addToOrderError?.response)) {
                setError(addToOrderError?.response?.errorMessage);
            } else {
                setError(t('DIALOG_NO_NETWORK_TEXT'));
            }
            setIsLoading(false);
            handleToggleConfirmationDialog();
        }
    }, [
        dispatch,
        handleOperation,
        handleToggleConfirmationDialog,
        logUserEvent,
        mappedItems,
        notes,
        notesFromState,
        orderId,
        t
    ]);

    const handleRetry = React.useCallback(() => {
        if (lastRequest) {
            lastRequest();
            setError('');
            handleToggleConfirmationDialog();
        }
    }, [handleToggleConfirmationDialog, lastRequest]);
    const handleConfirm = React.useCallback(() => {
        if (error) {
            handleRetry();
        }
    }, [error, handleRetry]);
    const handleErrorDialogClose = React.useCallback(() => {
        partialResetBasket(dispatch);
        orderApi.resetNonceByKey([EOrderNonceKey.ADD_ITEM_TO_ORDER]);
        push(ROUTES.JOURNEY.LANDING);
    }, [dispatch, push]);

    if (isLoading) {
        return (
            <StyledLoadingContainer>
                <RandomLoading
                    fallbackText={t('GENERAL_GENERIC_ERROR')}
                    enabledImages={[
                        ELoadingSVG.COFFEE_CUP,
                        ELoadingSVG.COFFEE_MUG,
                        ELoadingSVG.CUPCAKE,
                        ELoadingSVG.DONUT,
                        ELoadingSVG.FRYING_PAN,
                        ELoadingSVG.POPSICLE
                    ]}
                />
            </StyledLoadingContainer>
        );
    }

    return (
        <StyledBox marginX="auto" className="hidden-scroll">
            <Paper sx={{ minHeight: '100%', borderRadius: 0, paddingBottom: '50px' }}>
                <PayHeader variant="small">
                    <PayToolbar
                        loading={isMenuLoading}
                        table={tableNumber}
                        onBack={handleClickBack}
                        disableEdit
                    />
                </PayHeader>
                <Box pt={1}>
                    <PayItems
                        loading={isMenuLoading}
                        items={mappedItems}
                        onItemClick={handleItemClick}
                        keepOpen
                    />
                </Box>
                <PayOrderNotes loading={isCurrentLocationLoading} />
                <Box
                    position="fixed"
                    bottom="0"
                    width="100%"
                    p={1}
                    pb={0}
                    marginX="auto"
                    maxWidth={REDUCED_CONTENT_MAX_WIDTH}
                >
                    <ButtonV2
                        disabled={!isWorkingHour}
                        color="primary"
                        variant="contained"
                        className={classes.viewBasketButton}
                        onClick={handleAddOrder}
                        fullWidth
                        data-cy="pat-basket-submit"
                    >
                        {t('MPO_BASKET_SUBMIT_BTN')}
                    </ButtonV2>
                </Box>
            </Paper>
            <PayBaseModal open={quantitySelectProductIndex !== -1} onClose={handleCloseQuantitySelect}>
                <Box
                    width="100%"
                    height="100%"
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    pt={2}
                >
                    <QuantitySelector min={0} max={999} quantity={quantity} onChange={setQuantity} />

                    <ButtonV2
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateItemQuantity}
                    >
                        {quantity === 0 ? t('BASKET_REMOVE_ITEM') : t('PREORDER_UPDATE_BASKET_QUANTITY')}
                    </ButtonV2>
                </Box>
            </PayBaseModal>
            <ConfirmDialog
                open={isConfirmationOpen}
                disableBackdropClick
                disableEscapeKeyDown
                onClose={handleErrorDialogClose}
                confirmMessage={t('RETRY')}
                onConfirm={handleConfirm}
                description={t('DIALOG_ORDER_STATUS_UNKNOWN')}
            />
        </StyledBox>
    );
};
